'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@petsmart-ui/sparky';
import { useEffect, useRef, useState } from 'react';
import { VariantOption } from '../product-details';
import type { MiniPdpVariantGroup } from './model/use-mini-pdp-variants';
const SCROLL_EPSILON = 0.5;
type MiniPdpVariantRowProps = Readonly<{
  group: MiniPdpVariantGroup;
  onChange: (value: string) => void;
}>;
const getClusterPositions = (row: HTMLDivElement) => {
  const maxScrollLeft = Math.max(0, row.scrollWidth - row.clientWidth);
  const options = Array.from(row.children).map(option => option as HTMLElement);
  const positions = [0];
  let position = 0;
  while (position < maxScrollLeft - SCROLL_EPSILON) {
    const nextOption = options.find(option => option.offsetLeft + option.offsetWidth > position + row.clientWidth + SCROLL_EPSILON);
    const nextPosition = nextOption ? Math.min(Math.max(0, nextOption.offsetLeft), maxScrollLeft) : maxScrollLeft;
    if (nextPosition <= position + SCROLL_EPSILON) {
      positions.push(maxScrollLeft);
      break;
    }
    positions.push(nextPosition);
    position = nextPosition;
  }
  return positions;
};
export function MiniPdpVariantRow(t0) {
  const $ = _c(26);
  const {
    group,
    onChange
  } = t0;
  const rowRef = useRef(null);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const row = rowRef.current;
      if (!row) {
        return;
      }
      const syncScrollPosition = () => {
        const maxScrollLeft = Math.max(0, row.scrollWidth - row.clientWidth);
        setCanScrollPrevious(row.scrollLeft > SCROLL_EPSILON);
        setCanScrollNext(row.scrollLeft < maxScrollLeft - SCROLL_EPSILON);
      };
      row.addEventListener("scroll", syncScrollPosition, {
        passive: true
      });
      const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(syncScrollPosition);
      resizeObserver?.observe(row);
      syncScrollPosition();
      return () => {
        row.removeEventListener("scroll", syncScrollPosition);
        resizeObserver?.disconnect();
      };
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== group.variants.length) {
    t2 = [group.variants.length];
    $[1] = group.variants.length;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = target => {
      const row_0 = rowRef.current;
      if (!row_0) {
        return;
      }
      if (typeof row_0.scrollTo === "function") {
        row_0.scrollTo({
          left: target,
          behavior: "smooth"
        });
      } else {
        row_0.scrollLeft = target;
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const scrollTo = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const row_1 = rowRef.current;
      if (!row_1) {
        return;
      }
      const target_0 = getClusterPositions(row_1).find(position => position > row_1.scrollLeft + SCROLL_EPSILON);
      if (target_0 === undefined) {
        return;
      }
      scrollTo(target_0);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const scrollToNextOption = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const row_2 = rowRef.current;
      if (!row_2) {
        return;
      }
      const target_1 = getClusterPositions(row_2).findLast(position_0 => position_0 < row_2.scrollLeft - SCROLL_EPSILON);
      if (target_1 === undefined) {
        return;
      }
      scrollTo(target_1);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const scrollToPreviousOption = t5;
  const t6 = `mini-pdp-variant-row-${group.name}`;
  let t7;
  if ($[6] !== group.name || $[7] !== group.variants || $[8] !== onChange) {
    let t8;
    if ($[10] !== group.name || $[11] !== onChange) {
      t8 = variant => <VariantOption key={variant.value} value={variant.value} display={variant.display} variantType={group.name} isChecked={variant.isChecked} isUnavailable={variant.isUnavailable} handleChangeAction={() => onChange(variant.value)} />;
      $[10] = group.name;
      $[11] = onChange;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t7 = group.variants.map(t8);
    $[6] = group.name;
    $[7] = group.variants;
    $[8] = onChange;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div ref={rowRef} className="mini-pdp-drawer__variant-row" data-testid={t6}>{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== canScrollPrevious || $[17] !== group.name) {
    t9 = canScrollPrevious && <Button circle={true} type="button" text="Previous options" aria-label={`Previous ${group.name} options`} iconName="chevron-left" iconSize="sm" hideText={true} variant="primary" className="mini-pdp-drawer__variant-scroll-button mini-pdp-drawer__variant-scroll-button--previous" data-testid={`mini-pdp-variant-previous-${group.name}`} onClick={scrollToPreviousOption} />;
    $[16] = canScrollPrevious;
    $[17] = group.name;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== canScrollNext || $[20] !== group.name) {
    t10 = canScrollNext && <Button circle={true} type="button" text="Next options" aria-label={`Next ${group.name} options`} iconName="chevron-right" iconSize="sm" hideText={true} variant="primary" className="mini-pdp-drawer__variant-scroll-button mini-pdp-drawer__variant-scroll-button--next" data-testid={`mini-pdp-variant-next-${group.name}`} onClick={scrollToNextOption} />;
    $[19] = canScrollNext;
    $[20] = group.name;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8 || $[24] !== t9) {
    t11 = <div className="mini-pdp-drawer__variant-row-container">{t8}{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}